import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'components/Grid';
import AuthFormsWrapper from 'componentsShared/AuthFormsWrapper';
import Alert from 'components/Alert';
import Form, { FormItem, validationRules } from 'components/Form';
import Divider from 'components/Divider';
import { TextField } from 'components/TextField';
import useForm from 'hooks/useForm';
import {
  EResetPasswordCasesUI,
  TSignInAmplifyUI,
} from 'data/types/auth.types';
import { EUserRolesUI } from 'data/types/user.types';
import {
  EAuthenticationFlow,
  useLazyGetAuthenticationFlowsQuery,
  useLazyGetSsoConfigurationByEmailQuery,
  useResetPasswordCasesMutation,
  useSignInMutation,
} from 'redux/apiSlices/auth.slice';
import { RoutesNames } from 'routes/RoutesNames';
import get from 'lodash/get';
import Button from 'components/Button';
import { Paragraph, SecondaryText, Text, Title } from 'components/Typography';
import { Link, useLocation } from 'react-router-dom';
import { Colors } from 'styles/CssVariables';
import { useStateSafely } from 'hooks/useStateSafely';
import {
  EErrorCode,
  TAmplifyErrorPayload,
} from 'data/types/amplify.types';
import toast from 'components/Notification';
import { Auth } from 'aws-amplify';
import { loginFields } from './Login.data';
import SetPasswordForm from '../SetPasswordForm';
import Confirmation from '../Confirmation';
import MicrosoftLogo from 'assets/images/microsoft_logo.png';
import AdminLoginRedirectModal from 'routes/components/AdminLoginRedirectModal';
import { useAppSelector } from 'redux/store';

const { email, password } = loginFields;

const Login: FC = () => {
  const intl = useIntl();
  const { form } = useForm<{ username: string; password: string }>();
  const [error, setError] = useStateSafely<string>('');
  const alertRef = useRef<HTMLDivElement | null>(null);
  const [signIn, { isLoading, data }] = useSignInMutation();
  const [resetPassword] = useResetPasswordCasesMutation();
  const location = useLocation<{
    data: EResetPasswordCasesUI.RESEND_INVITATION;
  }>();
  const { state } = location;
  const [ssoLoading, setSsoLoading] = useState(false);
  const [authFlows, setAuthFlows] = useState<EAuthenticationFlow[]>([]);
  const [getAuthenticationFlows, { isFetching: isFlowsLoading }] = useLazyGetAuthenticationFlowsQuery();
  const [getSsoConfiguration] = useLazyGetSsoConfigurationByEmailQuery();
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedPassword, setSubmittedPassword] = useState('');
  const { userType, impersonateOn } = useAppSelector((state) => state.usersSlice);

  const sendInvitationHandler = async (email: string) => {
    try {
      await resetPassword({ email });
    } catch (err) {
      const error = err as TAmplifyErrorPayload;
      const message = get(error, 'data.message');
      setError(message);
    }
  };

  const onEmailSubmit = async () => {
    try {
      const emailValue = form.getFieldValue('username')?.trim();
      form.setFieldsValue({ username: emailValue });
      setError('');
      const flows = await getAuthenticationFlows({ emailAddress: emailValue }).unwrap();
      setAuthFlows(flows);
      setSubmittedEmail(emailValue);
    } catch (err: any) {
      const message = err?.data?.message || err?.message || 'Something went wrong';
      setError(message);
    }
  };

  const onSignIn = async (values: TSignInAmplifyUI) => {
    try {
      await signIn(values).unwrap();
    } catch (err) {
      const error = err as TAmplifyErrorPayload;
      const code = get(error, 'data.code');
      const message = get(error, 'data.message');
      const name = get(error, 'data.name');
      if (
        name === 'QuotaExceededError' ||
        message.includes('The quota has been exceeded')
      ) {
        setError(intl.formatMessage({ id: 'login.quotaExceededError' }));
        window.localStorage.clear();
      }
      if (
        code === EErrorCode.NOT_AUTHORIZED_EXCEPTION &&
        message?.includes('expired')
      ) {
        setError(
          intl.formatMessage(
            { id: 'login.temporaryPasswordExpired' },
            { email: values.email }
          )
        );
        await sendInvitationHandler(values.email);
      } else {
        setError(message);
      }
    }
  };

  const onSetPassword = async () => {
    try {
      const passwordValue = form.getFieldValue('password');
      const confirmValue = form.getFieldValue('passwordConfirm');
      if (passwordValue !== confirmValue) {
        setError('Passwords do not match');
        return;
      }
      setSignUpLoading(true);
      setError('');
      await Auth.signUp({
        username: submittedEmail,
        password: passwordValue,
        attributes: { email: submittedEmail },
      });
      setSubmittedPassword(passwordValue);
      setShowConfirmation(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setSignUpLoading(false);
    }
  };

  const onSsoSignIn = async () => {
    try {
      setSsoLoading(true);
      setError('');
      const ssoConfig = await getSsoConfiguration({ emailAddress: submittedEmail }).unwrap();

      // Build Cognito authorize URL manually to pass login_hint to Azure AD
      const authConfig = Auth.configure() as any;
      const { domain, redirectSignIn, responseType, scope } = authConfig.oauth;
      const clientId = authConfig.userPoolWebClientId;

      const params = new URLSearchParams({
        identity_provider: ssoConfig.identityProviderName,
        redirect_uri: redirectSignIn,
        response_type: responseType,
        client_id: clientId,
        scope: (scope as string[]).join(' '),
        login_hint: submittedEmail,
      });

      window.location.assign(`https://${domain}/oauth2/authorize?${params.toString()}`);
      // Browser will redirect — code below won't execute
    } catch (err: any) {
      const message = err?.data?.message || err?.message || 'Failed to retrieve SSO configuration';
      setError(message);
      setSsoLoading(false);
    }
  };

  const handleFormSubmit = (values: { username: string; password: string }) => {
    if (authFlows.length === 0) {
      onEmailSubmit();
    } else if (hasFlow(EAuthenticationFlow.SET_PASSWORD)) {
      onSetPassword();
    } else {
      onSignIn({ password: values.password, email: submittedEmail });
    }
  };

  const resetToEmailStep = () => {
    setAuthFlows([]);
    setSubmittedEmail('');
    setError('');
  };

  const hasFlow = (flow: EAuthenticationFlow) => authFlows.includes(flow);
  const emailStepActive = authFlows.length === 0;
  const showPasswordField = hasFlow(EAuthenticationFlow.LOGIN_PASSWORD) || hasFlow(EAuthenticationFlow.SET_PASSWORD);
  const showConfirmPasswordField = hasFlow(EAuthenticationFlow.SET_PASSWORD);
  const showSsoButton = hasFlow(EAuthenticationFlow.SSO);

  // SSO-only flow: only SSO is available, no password login
  const isSsoOnlyFlow = hasFlow(EAuthenticationFlow.SSO) && !hasFlow(EAuthenticationFlow.LOGIN_PASSWORD) && !hasFlow(EAuthenticationFlow.SET_PASSWORD);

  // Show divider when both password and SSO are available
  const showDivider = showPasswordField && showSsoButton;

  useEffect(() => {
    if (error) {
      alertRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [error]);

  useEffect(() => {
    if (localStorage.getItem('showToast')) {
      toast({
        description: intl.formatMessage({ id: 'login.sessionExpired' }),
        type: 'error',
      });
      localStorage.removeItem('showToast');
    }
  }, []);

  if (data?.challengeName === 'NEW_PASSWORD_REQUIRED') {
    return (
      <SetPasswordForm
        email={form.getFieldValue('username')}
        temporaryPassword={form.getFieldValue('password')}
      />
    );
  }

  if (showConfirmation) {
    return <Confirmation email={submittedEmail} password={submittedPassword} />;
  }

  if (userType === EUserRolesUI.ADMIN && !impersonateOn) {
    return <AdminLoginRedirectModal open />;
  }

  return (
    <Row justify="center">
      <AuthFormsWrapper
        title={
          isSsoOnlyFlow && !emailStepActive ? (
            <FormattedMessage id="login.ssoOnlyTitle" defaultMessage="Your organization uses Microsoft Single Sign-On." />
          ) : (
            <FormattedMessage id="login.welcomeBackTitle" />
          )
        }
      >
        {!isSsoOnlyFlow || emailStepActive ? (
          <Title fontLevel={5} className="mb-6">
            {emailStepActive ? (
              <FormattedMessage id="login.enterEmailToContinue" defaultMessage="Enter your email to continue" />
            ) : showPasswordField && showSsoButton ? (
              <FormattedMessage id="login.enterPasswordOrSso" defaultMessage="Enter your password or sign in with Microsoft" />
            ) : showPasswordField ? (
              <FormattedMessage id="login.enterCredentials" />
            ) : null}
          </Title>
        ) : null}
        {state?.data === EResetPasswordCasesUI.RESEND_INVITATION && (
          <Col span={24} ref={alertRef}>
            <Alert
              className="mb-4"
              type="info"
              message={<FormattedMessage id="login.emailResent" />}
            />
          </Col>
        )}
        <Form
          form={form}
          onFinish={handleFormSubmit}
          requiredMark={false}
          scrollToFirstError
        >
          <Row>
            <Col span={24} ref={alertRef}>
              <Alert type="error" message={error} />
            </Col>
            {emailStepActive && (
              <Col span={24}>
                <FormItem
                  name={email.name}
                  rules={email.rules}
                  label={<FormattedMessage id="login.form.emailLabel" />}
                >
                  <TextField
                    type="text"
                    name="username"
                    autoComplete="username"
                    inputMode="email"
                    placeholder={intl.formatMessage({
                      id: 'login.form.emailPlaceholder',
                    })}
                  />
                </FormItem>
              </Col>
            )}
            {showPasswordField && (
              <Col span={24}>
                <FormItem
                  name={password.name}
                  rules={password.rules}
                  label={
                    hasFlow(EAuthenticationFlow.SET_PASSWORD)
                      ? <FormattedMessage id="login.form.newPasswordLabel" defaultMessage="New password (Min. of 6 characters)" />
                      : <FormattedMessage id="login.form.passwordLabel" />
                  }
                >
                  <TextField
                    placeholder={intl.formatMessage({
                      id: 'login.form.passwordPlaceholder',
                    })}
                    type="password"
                    name="password"
                    autoComplete={
                      hasFlow(EAuthenticationFlow.SET_PASSWORD)
                        ? 'new-password'
                        : 'current-password'
                    }
                  />
                </FormItem>
              </Col>
            )}
            {showConfirmPasswordField && (
              <Col span={24}>
                <FormItem
                  name="passwordConfirm"
                  rules={[
                    ...validationRules.passwordConfirm,
                    validationRules.required('confirm password'),
                  ]}
                  label={<FormattedMessage id="login.form.confirmPasswordLabel" defaultMessage="Repeat the password" />}
                >
                  <TextField
                    placeholder={intl.formatMessage({
                      id: 'login.form.confirmPasswordPlaceholder',
                      defaultMessage: 'Repeat the password',
                    })}
                    type="password"
                  />
                </FormItem>
              </Col>
            )}
            <Col span={24}>
              {emailStepActive ? (
                <Button htmlType="submit" loading={isFlowsLoading} style={{ width: '50%' }}>
                  <FormattedMessage id="login.continue" defaultMessage="Continue" />
                </Button>
              ) : (
                <>
                  {hasFlow(EAuthenticationFlow.SET_PASSWORD) && (
                    <Button htmlType="submit" loading={signUpLoading} style={{ width: '50%' }}>
                      <FormattedMessage id="login.createAccount" defaultMessage="Create account" />
                    </Button>
                  )}
                  {hasFlow(EAuthenticationFlow.LOGIN_PASSWORD) && (
                    <Button htmlType="submit" loading={isLoading} style={{ width: '50%' }}>
                      <FormattedMessage id="login.signIn" />
                    </Button>
                  )}
                  {showDivider && <Divider > <SecondaryText className='mx-2'>or </SecondaryText> </Divider>}
                  {showSsoButton && (
                    <Button
                      style={{
                        backgroundColor: '#0078D4',
                        borderColor: '#0078D4',
                        color: 'white',
                        width: isSsoOnlyFlow ? '100%' : '50%',
                        marginBottom: '8px',
                        marginTop: !showDivider ? '32px' : '0',
                      }}
                      onClick={onSsoSignIn}
                      loading={ssoLoading}
                      icon={<img src={MicrosoftLogo} alt="Microsoft" style={{ width: 20, height: 20, marginRight: "8px" }} />}
                    >
                      <FormattedMessage id="login.signInWithMicrosoft" defaultMessage="Sign in with Microsoft" />
                    </Button>
                  )}
                  <Paragraph className="mt-2" fontLevel={5}>
                    <FormattedMessage id="login.notYourEmail" defaultMessage="Not your email?" />{' '}
                    <Text
                      color={Colors.DarkBlue}
                      fontLevel={5}
                      underline
                      style={{ cursor: 'pointer' }}
                      onClick={resetToEmailStep}
                    >
                      <FormattedMessage id="login.useDifferentEmail" defaultMessage="Use a different email" />
                    </Text>
                  </Paragraph>
                  {hasFlow(EAuthenticationFlow.LOGIN_PASSWORD) && (
                    <Paragraph className="mt-2" fontLevel={5}>
                      <FormattedMessage id="login.forgotPassword" />{' '}
                      <Text color={Colors.DarkBlue} fontLevel={5} underline>
                        <Link to={{ pathname: RoutesNames.ResetPassword, state: { email: submittedEmail } }}>
                          <FormattedMessage id="login.resetItHere" />{' '}
                        </Link>
                      </Text>
                    </Paragraph>
                  )}
                </>
              )}
              {emailStepActive && (
                <Paragraph className="mt-2" fontLevel={5}>
                  <FormattedMessage id="login.dontHaveAnAccount" />{' '}
                  <Text color={Colors.DarkBlue} fontLevel={5} underline>
                    <a
                      target="_blank"
                      href="https://www.ourpetpolicy.com/demo"
                      rel="noreferrer"
                    >
                      <FormattedMessage id="login.requestDemo" />{' '}
                    </a>
                  </Text>
                  <FormattedMessage id="login.setupAnAccount" />
                </Paragraph>
              )}
            </Col>
          </Row>
        </Form>
      </AuthFormsWrapper>
    </Row>
  );
};

export default Login;
